/**
 * Resolve which tracks and roles contribute to role leveling / mastery.
 */

/**
 * @param {import('../services/rolesService').Role[]|null|undefined} roles
 * @param {{ excludeRoleId?: string }} [options]
 * @returns {Set<string>}
 */
export function collectTrackIdsAssignedToRoles(roles, options = {}) {
  const { excludeRoleId } = options;
  const assigned = new Set();
  for (const role of roles || []) {
    if (excludeRoleId && role.roleId === excludeRoleId) continue;
    for (const step of role.tracks || []) {
      if (step?.trackId) assigned.add(step.trackId);
    }
  }
  return assigned;
}

/**
 * Tracks in the published catalog that no career role maps to (excluding programmer catch-all).
 * @param {Array<{ trackId?: string }>|null|undefined} allTracks
 * @param {Set<string>} assignedTrackIds
 * @returns {string[]}
 */
export function getOrphanTrackIds(allTracks, assignedTrackIds) {
  return (allTracks || [])
    .map((t) => (t?.trackId != null ? String(t.trackId).trim() : ''))
    .filter((id) => id && !assignedTrackIds.has(id));
}

/** @param {import('../services/rolesService').Role|null|undefined} role */
export function roleIncludesOrphanTrackSnippets(role) {
  return Boolean(role?.includesOrphanTracks);
}

/**
 * Extra track ids whose snippets count toward a catch-all role (e.g. programmer).
 * @param {import('../services/rolesService').Role} role
 * @param {Array<{ trackId?: string }>} allTracks
 * @param {import('../services/rolesService').Role[]} allRoles
 * @returns {string[]}
 */
export function getExtraTrackIdsForRole(role, allTracks, allRoles) {
  if (!roleIncludesOrphanTrackSnippets(role)) return [];
  const assigned = collectTrackIdsAssignedToRoles(allRoles, { excludeRoleId: role.roleId });
  return getOrphanTrackIds(allTracks, assigned);
}

/**
 * All track ids used for role snippet mastery (explicit path + orphan catch-all).
 * @param {import('../services/rolesService').Role} role
 * @param {Array<{ trackId?: string }>} allTracks
 * @param {import('../services/rolesService').Role[]} allRoles
 * @returns {string[]}
 */
export function getAllTrackIdsForRoleMastery(role, allTracks, allRoles) {
  const explicit = (role.tracks || []).map((s) => s.trackId).filter(Boolean);
  const extra = getExtraTrackIdsForRole(role, allTracks, allRoles);
  return [...new Set([...explicit, ...extra])];
}

/**
 * Whether a role track step includes the studied cookbook node.
 * Empty `nodeIds` on the step means the whole track counts.
 *
 * @param {import('../services/rolesService').RoleTrackStep|{ nodeIds?: string[] }} step
 * @param {string|null|undefined} nodeId
 * @returns {boolean}
 */
export function trackStepIncludesStudyNode(step, nodeId) {
  const filters = step?.nodeIds;
  if (!Array.isArray(filters) || filters.length === 0) return true;
  const nid = nodeId != null ? String(nodeId).trim() : '';
  if (!nid) return true;
  return filters.some((id) => id != null && String(id).trim() === nid);
}

/**
 * Role ids that should gain EXP when studying a snippet on `trackId` (and optional `nodeId`).
 * When a role track step lists `nodeIds`, only matching cookbooks count; empty = whole track.
 *
 * @param {string} trackId
 * @param {import('../services/rolesService').Role[]} roles
 * @param {string|null|undefined} [nodeId]
 * @returns {string[]}
 */
export function resolveContributingRoleIdsForTrack(trackId, roles, nodeId = null) {
  const id = trackId != null ? String(trackId).trim() : '';
  if (!id || !roles?.length) return [];

  const contributing = new Set();
  for (const role of roles) {
    const matches = (role.tracks || []).some(
      (step) => step.trackId === id && trackStepIncludesStudyNode(step, nodeId)
    );
    if (matches) contributing.add(role.roleId);
  }

  const catchAllRole = roles.find((r) => roleIncludesOrphanTrackSnippets(r));
  if (catchAllRole) {
    const assignedElsewhere = collectTrackIdsAssignedToRoles(roles, {
      excludeRoleId: catchAllRole.roleId
    });
    if (!assignedElsewhere.has(id)) {
      contributing.add(catchAllRole.roleId);
    }
  }

  return Array.from(contributing);
}
